/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Shown when the platform has locked this workspace out.
 *
 * Before this existed, a suspended tenant's requests came back 401, the auth
 * interceptor refreshed-then-signed-out, and the user landed on the login
 * page with no explanation -- identical to an ordinary expired session, and
 * they would simply log in again and fail again. The backend now answers 403
 * with a machine-readable code so we can say what actually happened.
 *
 * Deliberately not a route: it is rendered in place of the application by
 * App.js when the auth context carries a lockout, so it cannot be navigated
 * away from while the lockout stands.
 */

import PropTypes from "prop-types";
import { Box, Container } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";

const COPY = {
  TENANT_SUSPENDED: {
    title: "Workspace suspended",
    body:
      "Access to this workspace has been paused. This usually means a subscription " +
      "has lapsed. Your data is safe and returns the moment the workspace is reactivated.",
  },
  TENANT_ARCHIVED: {
    title: "Workspace archived",
    body:
      "This workspace has been archived and is no longer available for sign-in. " +
      "Contact your administrator if you believe this is a mistake.",
  },
  TENANT_INACTIVE: {
    title: "Workspace inactive",
    body: "This workspace is not currently active. Contact your administrator to restore access.",
  },
};

function formatDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
}

function TenantLocked({ lockout }) {
  const theme = useTheme();
  const { signout } = useAuth();

  const copy = COPY[lockout?.errorCode] || COPY.TENANT_INACTIVE;
  const since = formatDate(lockout?.suspendedAt);

  return (
    <MDBox
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.background.default,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <MDBox
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: "24px",
            boxShadow: theme.boxShadows?.lg,
            px: { xs: 3, sm: 6 },
            py: { xs: 5, sm: 7 },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              margin: "0 auto 24px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.white?.main || "#fff",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 34 }} />
          </Box>

          <MDTypography variant="h4" fontWeight="bold" sx={{ mb: 1.5 }}>
            {copy.title}
          </MDTypography>

          <MDTypography variant="body2" color="text" sx={{ mb: 3, lineHeight: 1.7 }}>
            {copy.body}
          </MDTypography>

          {/* The operator's own words, when they left any. Far more useful
              than the generic copy above. */}
          {lockout?.reason && (
            <MDBox
              sx={{
                backgroundColor: theme.palette.grey[200],
                borderRadius: "12px",
                px: 2.5,
                py: 2,
                mb: 3,
                textAlign: "left",
              }}
            >
              <MDTypography
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                color="text"
                sx={{ letterSpacing: "0.5px" }}
              >
                Reason
              </MDTypography>
              <MDTypography variant="body2" sx={{ mt: 0.5 }}>
                {lockout.reason}
              </MDTypography>
              {since && (
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                  Since {since}
                </MDTypography>
              )}
            </MDBox>
          )}

          <MDButton variant="gradient" color="info" onClick={signout} fullWidth>
            Sign out
          </MDButton>
        </MDBox>
      </Container>
    </MDBox>
  );
}

TenantLocked.propTypes = {
  lockout: PropTypes.shape({
    errorCode: PropTypes.string,
    status: PropTypes.string,
    message: PropTypes.string,
    reason: PropTypes.string,
    suspendedAt: PropTypes.string,
  }),
};

TenantLocked.defaultProps = { lockout: null };

export default TenantLocked;
